import { Injectable } from '@angular/core';
import {
  Params,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
} from '@angular/router';
import { AppConfigService } from '@core/services/app-config.service';
import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { SHA256, enc } from 'crypto-js';
import { catchError, map, Observable, of, take } from 'rxjs';
import { SessionService } from './session.service';
import jwt_decode from 'jwt-decode';
import { EuaJwtModel } from '@entities/session/session.model';

@Injectable({
  providedIn: 'root',
})
export class AuthUrlService {
  // need to disable this lint rule for auth parameter names
  /* eslint-disable @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match */
  NONCE_LENGTH = 32;
  CODE_CHALLENGE_LENGTH = 43;
  IA_IAM_DOMAIN = 'partner-pnc';
  EMPLOYEE_IAM_DOMAIN = 'employee';
  TRAMPOLINE_URL = 'https://nwxv2-trampoline-test.apps.nwie.net/';

  constructor(
    private appConfig: AppConfigService,
    private window: Window,
    private sessionService: SessionService
  ) {}

  get redirect_uri(): string {
    return (
      this.window.location.protocol + '//' + this.window.location.host + '/'
    );
  }

  buildEuaUrlForAgencyRoute(
    params: Params,
    authType: 'internal' | 'external'
  ): string {
    const EUA_URL = this.appConfig.config.euaAuthCodeUrl;
    const CLIENT_ID = this.appConfig.config.apiKey;
    const SCOPE = 'nwapi openid pci';
    const RESPONSE_TYPE = 'code';
    const NONCE = this.nonce(this.NONCE_LENGTH);
    const verifier = this.randomString(this.CODE_CHALLENGE_LENGTH);
    const codeChallenge = SHA256(verifier).toString(enc.Base64url);
    const CODE_CHALLENGE_METHOD = 'S256';

    sessionStorage.setItem('verifier', verifier);

    const queryString = this.window.btoa(this.paramsToQueryString(params));

    const query = {
      response_type: RESPONSE_TYPE,
      client_id: CLIENT_ID,
      redirect_uri: this.redirect_uri,
      state: queryString,
      scope: SCOPE,
      nonce: NONCE,
      code_challenge: codeChallenge,
      code_challenge_method: CODE_CHALLENGE_METHOD,
    } as { [key: string]: string };

    const pairs = [];
    for (const key of Object.keys(query)) {
      pairs.push(
        `${encodeURIComponent(key)}=${encodeURIComponent(query[key])}`
      );
    }

    const qs = pairs.join('&');
    const euaUrl =
      EUA_URL.replace('{iam_domain}', this.getIamDomain(authType)) + '?' + qs;
    return euaUrl;
  }

  performAuthRedirect(
    agencyChannel: string,
    route: ActivatedRouteSnapshot,
    routerState: RouterStateSnapshot | null
  ): void {
    let authType: 'internal' | 'external';
    if (agencyChannel === 'NSS') {
      authType = 'internal';
    } else {
      authType = 'external';
    }
    const params = {
      agencyChannel,
      requestedRoute: routerState?.url || route.routeConfig?.path || '',
      ...route.params,
      ...route.queryParams,
    };
    const euaUrl = this.buildEuaUrlForAgencyRoute(params, authType);
    // redirect for user auth
    this.window.location.href = euaUrl;
  }

  parseRoute(route: ActivatedRouteSnapshot): { [key: string]: string } {
    const params = route.queryParams!;
    if (!params) {
      return {};
    }

    const queryParams: { [key: string]: string } = {};
    Object.entries(params).forEach(([key, value]: [string, string]) => {
      if (key === 'state') {
        try {
          value = this.window.atob(value);
        } catch (e) {
          console.error('incoming EUA state not base-64', value, e);
        }
        const key1 = value.slice(0, value.indexOf('='));
        const requestedRoute = value.slice(value.indexOf('=') + 1);
        queryParams[key1] = decodeURIComponent(requestedRoute);
      } else {
        queryParams[key] = value;
      }
    });

    return queryParams;
  }

  getRoutePath(queryParams: string): string {
    const httpParams = new HttpParams({ fromString: queryParams });
    return httpParams.get('route') || '';
  }

  private nonce(length: number): string {
    let nonce = '';
    const possible =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    for (let i = 0; i < length; i++) {
      nonce += possible.charAt(Math.floor(Math.random() * possible.length));
    }
    return nonce;
  }

  private randomString(length: number): string {
    let randomString = '';
    const possible =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    for (let i = 0; i < length; i++) {
      randomString += possible.charAt(
        Math.floor(Math.random() * possible.length)
      );
    }
    return randomString;
  }

  private getIamDomain(authType: 'internal' | 'external'): string {
    if (authType === 'external') {
      return this.IA_IAM_DOMAIN;
    } else {
      return this.EMPLOYEE_IAM_DOMAIN;
    }
  }

  private paramsToQueryString(params: Params): string {
    const redirectUrl = params.requestedRoute || '/';
    let separator = redirectUrl.indexOf('?') >= 0 ? '&' : '?';
    let ret = Object.keys(params)
      .filter((key) => key !== 'requestedRoute')
      .reduce((acc, key) => {
        const result = `${acc}${separator}${encodeURIComponent(
          key
        )}=${encodeURIComponent(params[key])}`;
        separator = '&';
        return result;
      }, redirectUrl);

    ret = encodeURIComponent(ret);
    ret = 'requestedRoute=' + ret;
    return ret;
  }

  exchangeAuthorizationCode(
    authCode: string
  ): Observable<{ accessToken: string; jwt: EuaJwtModel }> {
    const verifier = this.sessionService.fetchVerifier();

    if (!verifier) {
      throw 'Could not get verifier from local storage using the state';
    }

    return this.sessionService
      .fetchAccessTokenWithAuthCode(authCode, verifier, this.redirect_uri)
      .pipe(
        take(1),
        map((result) => {
          if (
            result.access_token &&
            result.refresh_token &&
            result.id_token &&
            result.expires_in
          ) {
            const accessToken = result.access_token;
            const refreshToken = result.refresh_token;
            if (accessToken) {
              this.sessionService.storeToken(accessToken, 'access_token');
              this.sessionService.updateToken(accessToken, 'access_token');
            }
            if (refreshToken) {
              this.sessionService.storeToken(refreshToken, 'refresh_token');
              this.sessionService.updateToken(refreshToken, 'refresh_token');
            }
            const jwt = jwt_decode<EuaJwtModel>(result.id_token);
            if (jwt) {
              this.sessionService.storeJwt(jwt);
            }
            this.window.sessionStorage.setItem(
              'expires_in',
              result.expires_in.toString()
            );
            return { accessToken: result.access_token, jwt };
          }
          return {
            accessToken: result.access_token || '',
            jwt: {} as EuaJwtModel,
          };
        }),
        catchError((error) => {
          if (error instanceof HttpErrorResponse) {
            console.error('Error exchanging authorization code', error);
          }
          return of({ accessToken: '', jwt: {} as EuaJwtModel });
        })
      );
  }

  exchangeRefreshToken(
    refreshToken: string
  ): Observable<{
    accessToken: string;
    refreshToken: string;
    expires_in?: number;
  }> {
    return this.sessionService
      .fetchAccessTokenWithRefreshToken(refreshToken)
      .pipe(
        take(1),
        map((result) => {
          if (
            result.access_token &&
            result.refresh_token &&
            result.expires_in
          ) {
            const accessToken = result.access_token;
            const refreshToken = result.refresh_token;
            if (accessToken) {
              this.sessionService.storeToken(accessToken, 'access_token');
              this.sessionService.updateToken(accessToken, 'access_token');
            }
            if (refreshToken) {
              this.sessionService.storeToken(refreshToken, 'refresh_token');
              this.sessionService.updateToken(refreshToken, 'refresh_token');
            }
            this.window.sessionStorage.setItem(
              'expires_in',
              result.expires_in.toString()
            );
            return {
              accessToken: result.access_token,
              refreshToken: result.refresh_token,
              expires_in: result.expires_in,
            };
          }
          return { accessToken: result.access_token || '', refreshToken: '' };
        }),
        catchError((error) => {
          if (error instanceof HttpErrorResponse) {
            console.error('Error exchanging refresh token', error);
          }
          return of({ accessToken: '', refreshToken: '' });
        })
      );
  }
}
